import {useEffect, useRef, useState} from 'react';

import {QueryResultType} from 'graphql-client/BenchlingQuery';

export enum ViewState {
  LOADING = 'LOADING',
  ERROR = 'ERROR',
  COORDINATE_STRUCTURES_LIST = 'COORDINATE_STRUCTURES_LIST',
  RUN_STRUCTURE_PREDICTION = 'RUN_STRUCTURE_PREDICTION',
  VIEWER = 'VIEWER',
}

export interface UseStructurePredictionViewStateInput {
  /** Status of the structure prediction query. */
  structurePredictionQueryStatus: QueryResultType;
  /** True if there is at least one completed, viewable structure. */
  hasCompletedStructures: boolean;
  /** True if there is at least one in-progress prediction. */
  hasInProgressPredictions: boolean;
  /**
   * A view to force on first load, overriding the structure/in-progress heuristic. Captured once so it
   * still applies after the caller clears it from props, which can happen before the data settles.
   */
  initialView?: ViewState;
}

/**
 * Owns which pane a structure-prediction tab shows.
 *
 * On first load it resolves a view once the backing data
 * settles: an explicit initialView wins; otherwise a completed structure opens the viewer, an
 * in-progress prediction opens the list, and an entity with neither opens the run form. After that
 * first resolution the caller drives the view directly through setViewState.
 */
export function useStructurePredictionViewState({
  structurePredictionQueryStatus,
  hasCompletedStructures,
  hasInProgressPredictions,
  initialView,
}: UseStructurePredictionViewStateInput): {
  viewState: ViewState;
  setViewState: (viewState: ViewState) => void;
} {
  const [viewState, setViewState] = useState<ViewState>(ViewState.LOADING);
  const [initialLoadCompleted, setInitialLoadCompleted] = useState(false);
  const initialViewRef = useRef(initialView);

  useEffect(() => {
    if (initialLoadCompleted) {
      return;
    }
    if (structurePredictionQueryStatus === QueryResultType.ERROR) {
      setInitialLoadCompleted(true);
      setViewState(ViewState.ERROR);
      return;
    }
    if (structurePredictionQueryStatus !== QueryResultType.SUCCESS) {
      // PENDING or SKIPPED: keep waiting so a caller that skips the query until its inputs are
      // ready still resolves once the query later succeeds.
      setViewState(ViewState.LOADING);
      return;
    }
    setInitialLoadCompleted(true);
    const requestedView = initialView ?? initialViewRef.current;
    if (requestedView != null) {
      initialViewRef.current = undefined;
      setViewState(requestedView);
    } else if (hasCompletedStructures) {
      setViewState(ViewState.VIEWER);
    } else if (hasInProgressPredictions) {
      setViewState(ViewState.COORDINATE_STRUCTURES_LIST);
    } else {
      setViewState(ViewState.RUN_STRUCTURE_PREDICTION);
    }
  }, [
    structurePredictionQueryStatus,
    hasCompletedStructures,
    hasInProgressPredictions,
    initialLoadCompleted,
    initialView,
  ]);

  return {viewState, setViewState};
}
